<template>
<div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mx-3 border-bottom">
	<h4 v-if="isNoConnectionToServer">Немає св'язку з сервером</h4>
	<h4 v-else-if="isNoStation">Станція катодного захисту не знайдена</h4>
	<div v-else-if="station !== undefined">
		<span class="fs-5">
			Станція катодного захисту <wbr>
			<b class="fw-bold text-nowrap">
				{{ `"${station.passport.get('name')}"`}}
			</b>
		</span>
	</div>
	<div v-else>
		<span class="fs-5">
			Не можемо знайти станцію катодного захисту. Спробуйте ще, або зверніться до адміністратора.
		</span>
	</div>

	<div v-show="['station_settings', 'station_passport'].includes($route.name)">
		<span class="d-inline-block" tabindex="0" data-bs-toggle="tooltip" data-bs-title="історія змін">
			<button
				v-if="accountStore.isPrimary"
				type="button"
				class="btn btn-sm btn-outline-secondary m-0 me-2 p-0"
				@click="$router.push({ name: 'station_passport_history',}); updateSections();"
				style="border: 1; width: 32px; aspect-ratio: 1;"
			>
				<button-icon icon="clock-history" color="currentColor" edge-size="20" />
			</button>
		</span>

		<span class="d-inline-block" tabindex="0" data-bs-toggle="tooltip" data-bs-title="таблиця налаштувань">
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary m-0 p-0"
				@click="$router.push({ name: 'station_passport_table',}); updateSections();"
				style="border: 1; width: 32px; aspect-ratio: 1;"
			>
				<button-icon icon="table" color="currentColor" edge-size="20" />
			</button>
		</span>
	</div>
</div>

<section v-if="stationsStore.isNoStations || isLoading" class="w-100 h-100 m-0 p-0">
	<div class="d-flex flex-column justify-content-center align-items-center h-100 w-100">
		<div class="spinner-border text-secondary app-spinner" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</div>
</section>
<section v-else class="w-100 m-0 p-0 px-1">
	<!-- Check if on mobile device and only decktop page -->
	<div
		v-if="
			!['xxl', 'xl', 'lg'].includes(breakpoint)
			&& ['station_analytic', 'station_manage', 'station_view_report', 'station_security', 'station_lightnings'].includes($route.name)
		"
		class="d-flex flex-column justify-content-center align-items-center h-100 w-100"
	>
		<p class="fs-5 text-center">
			Дану сторінку можна переглядати тільки на великих екранах
		</p>
	</div>
	<!-- set stationId and objectId for station sections and common sections compatibility -->
	<router-view
		v-else-if="['station_log', 'post_log'].includes($route.name)"
		:objectId="stationId"
	>
	</router-view>
	<router-view
		v-else
		:stationId="stationId"
	>
	</router-view>
</section>
</template>




<script>
import { useTooltip } from '@/composables/bootstrap-tooltip.js'
import ButtonIcon from '@/components/Icons/ButtonIcon.vue'
import { useSizeBreakpoint } from '@/composables/size-breakpoint.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import { useStationsHistoryStore } from '@/stores/StationsHistoryStore.js'
import { useAlerts } from '@/composables/alerts.js'
import { CustomNavTabs, EmptyNavSection } from '@/customNavSection.js'
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { ElectricMeter } from '@/models/electricMeter.js'
import { ModemUnit } from '@/models/modemUnit.js'
import { INFLUXDB } from '@/api/api.js'
import {
	subtractHoursFromNowDate,
	firstYearDay,
	lastYearDay,
	dayStart,
} from '@/helpers.js'


export default {
	METRICS_UPDATE_RATE: 30 * 1000,

	inject: ['customNavSection', ],

	components: {
		ButtonIcon,
	},

	props: {
	},

	setup() {
		const { breakpoint } = useSizeBreakpoint()
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const historyStore = useStationsHistoryStore()
		const {
			allAlerts,
			setFilters: setAlertFilters,
		} = useAlerts()

		useTooltip()

		return {
			breakpoint,
			accountStore,
			stationsStore,
			historyStore,

			allAlerts,
			setAlertFilters,
		}
	},

	data() {
		return {
			// State flags
			isLoading: false,
			isNoStation: false,
			isNoConnectionToServer: false,

			// Metrics update timer
			metricsUpdateInterval: null,
			historyUpdateInterval: null,

			//
			stationId: undefined,
			//
			alertsTabMeta: {
				sup: 0 // superscripted text
			},
		}
	},

	watch: {
		route(/*newRoute*/) {
			// console.log('route change to', newRoute)
			this.updateSections()
		},

		allAlerts() {
			this.alertsTabMeta.sup = this.allAlerts.length
		}
	},

	async created() {
		// console.log('LoadStationView')
		// console.time('LoadStationView')
		this.isLoading = true
		const nowYear = Number((new Date()).getFullYear())
		// Create custom navigation
		this.customNavSection = new CustomNavTabs()

		this.updateSections()

		// Read id from url
		try {
			this.stationId = this.$route.params.id
		} catch {
			console.error("On station id read.")
			this.isNoConnectionToServer = true
			return
		}

		// Load station
		await this.updateStation()

		this.historyStore.loadMetrics(
			this.station.passport.get(Station.passportKeys.metricsId),
			[
				...this.activeSecurityCircuits.map((el) => [INFLUXDB.station_metrics.service_unit, el]),
				[INFLUXDB.station_metrics.service_unit, ServiceUnit.metricKeys.fSecurityCircuitBoxDoor],
				[INFLUXDB.station_metrics.power_unit, PowerUnit.metricKeys.isConnect],
				[INFLUXDB.station_metrics.service_unit, ServiceUnit.metricKeys.isConnect],
				[INFLUXDB.station_metrics.modem_unit, ModemUnit.metricKeys.isConnect],
				[INFLUXDB.station_metrics.electric_meter_unit, ElectricMeter.metricKeys.isConnect],
			],
			firstYearDay(nowYear),
			lastYearDay(nowYear),
			true,
		)

		this.updateSections()

		// Read station metrics and alerts
		// Updates every * seconds
		this.metricsUpdateInterval = setInterval(async () => {
			this.updateStation()
		}, this.$options.METRICS_UPDATE_RATE);

		// console.timeEnd('LoadStationView')
		this.isLoading = false
	},

	methods: {
		async updateStation() {
			// console.log('Update station')
			
			// Read metrics
			await this.stationsStore.readMetrics((result) => {
				if (result) {
					this.isNoConnectionToServer = false
					const station = this.stationsStore.station(this.stationId)
					this.isNoStation = station == undefined
					if (!this.isNoStation) {
						document.title = station.passport.has(Station.passportKeys.name) ?
							station.passport.get(Station.passportKeys.name) : "Станція"
					}
				} else {
					console.error("No connection on read Metrics")
					this.isNoConnectionToServer = true
				}
			},
			this.stationId)

			// Set filter for read alerts
			this.setAlertFilters({
				fromTime: dayStart(subtractHoursFromNowDate(24)),
				toTime: new Date(),
				stationIds: [this.stationId, ],
			})

			// Update passport
			await this.stationsStore.readPassport(undefined, this.stationId)
		},

		updateSections() {
			// Create custom navigation data model
			let control = false, alerts = false, electricMeter=false, breaker=false, protection=false,
				manage = false, analytic = false, view_report = false, security=false, lightnings=false,
				download_report = false, log = false, settings = false, calibration=false, passport = false,
				history = false, passport_table = false
			// const electricMeterPassport = this.station?.passport.get(Station.passportKeys.electricMeter.self)
			this.tabs.clean()

			// Add tabs
			this.tabs.addLine('station_control', 'Телеконтроль', null, control);
			this.tabs.addLine('station_alerts', 'Сповіщення за 24г ', null, alerts, undefined, this.alertsTabMeta);
			// If electric meter is configured - make enabled
			if (this.station?.electricMeterIsSeparateUnit || this.station?.electricMeterIsServiceUnit) {
				this.tabs.addLine('electric_meter_control', 'Електролічильник', null, electricMeter)
			} else {
				this.tabs.addLine('electric_meter_control', 'Електролічильник', null, electricMeter, undefined, { permission: 'disabled' })
			}
			if (this.station?.breaker.isPresent) {
				this.tabs.addLine('station_breaker', 'Переривач', null, breaker)
			} else {
				this.tabs.addLine('station_breaker', 'Переривач', null, breaker, undefined, { permission: 'disabled' })
			}
			this.tabs.addLine('station_protection', 'Захищеність', null, protection)
			this.tabs.addLine('station_manage', 'Телекерування', null, manage)
			this.tabs.addLine('station_analytic', 'Аналітики', null, analytic)
			this.tabs.addLine('station_view_report', 'Аналіз даних', null, view_report)
			this.tabs.addLine('station_security', 'Охорона', null, security)
			this.tabs.addLine('station_lightnings', 'Блискавки', null, lightnings)
			this.tabs.addLine('station_download_report', 'Звіти', null, download_report)
			this.tabs.addLine('station_log', 'Лоґ', null, log, undefined, { permission: 'view_log' })
			this.tabs.addLine('station_settings', 'Налаштування', null, settings)
			this.tabs.addLine('station_calibration', 'Калібрування', null, calibration, undefined, { permission: 'calibrate_stations' })
			this.tabs.addLine('station_passport', 'Паспорт', null, passport)
			if (this.route == 'station_passport_history') {
				this.tabs.addLine('station_passport_history', 'Історія', null, history)
			}
			if (this.route == 'station_passport_table') {
				this.tabs.addLine('station_passport_table', 'Таблиця налаштувань', null, passport_table)
			}

			// Marck active tab on page load
			this.tabs.checkTab(this.route)
		}
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		metricsId() { return this.station.passport.get(Station.passportKeys.metricsId) },

		route() {
			return this.$route.name
		},

		tabs() {
			return this.customNavSection
		},

		activeSecurityCircuits() {
			return this.station.service_unit.activeSecurityCircuits.map(
				(circuitNum) => `f_security_circuit_${circuitNum}`
			)
		},
	},

	beforeUnmount() {
		// Clear custom nav section
		this.customNavSection = new EmptyNavSection()
		
		if (this.metricsUpdateInterval != null) {
			clearInterval(this.metricsUpdateInterval)
			this.metricsUpdateInterval = null
		}

		if (this.historyUpdateInterval != null) {
			clearInterval(this.historyUpdateInterval)
			this.historyUpdateInterval = null
		}
	},
}
</script>




<style src="@/assets/widget.css" scoped></style>
<style scoped>
</style>
